import React, { useState, useMemo } from 'react';
import './TransactionsTable.css';

const TransactionsTable = ({ transactions, onEdit, onDelete, onDuplicate, onAdd, onToggleStatus }) => {
  const [sortKey, setSortKey]   = useState('date');
  const [sortDir, setSortDir]   = useState('desc');
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Reset pagination when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [transactions]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try { return new Date(dateString + 'T12:00:00').toLocaleDateString('pt-BR'); }
    catch { return dateString; }
  };

  const isIncome = (type) => ['receita', 'entrada'].includes((type || '').toLowerCase());
  const getStatusClass = (status) => ['pago', 'recebido'].includes((status || '').toLowerCase()) ? 'paid' : 'pending';

  const isSafeUrl = (url) => {
    if (!url) return false;
    try {
      const parsed = new URL(url);
      return ['https:', 'http:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  // ── Ordenação ──────────────────────────────────────────────────
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const sorted = useMemo(() => {
    const getValue = (t, key) => {
      // Normalização pt/en
      const map = {
        date: t.date || t.data,
        description: t.description || t.descrição,
        category: t.category || t.categoria,
        value: parseFloat(t.value || t.valor || 0),
        status: t.status,
      };
      return map[key] ?? '';
    };

    return [...(transactions || [])].sort((a, b) => {
      let va = getValue(a, sortKey);
      let vb = getValue(b, sortKey);
      
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [transactions, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (!transactions || transactions.length === 0) {
    return (
      <div className="tt-empty">
        <span>📋</span>
        <p>Nenhuma transação encontrada</p>
        {onAdd && (
          <button className="tt-empty-btn" onClick={onAdd} style={{ marginTop: '10px', padding: '8px 16px', borderRadius: '20px', background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
            + Criar Minha Primeira
          </button>
        )}
      </div>
    );
  }

  const PaginationControls = () => (
    <div className="tt-pagination">
      <button 
        disabled={currentPage === 1} 
        onClick={() => setCurrentPage(p => p - 1)}
        className="tt-page-btn"
      >
        ◀ Anterior
      </button>
      <span className="tt-page-info">Página {currentPage} de {totalPages}</span>
      <button 
        disabled={currentPage === totalPages} 
        onClick={() => setCurrentPage(p => p + 1)}
        className="tt-page-btn"
      >
        Próxima ▶
      </button>
    </div>
  );

  return (
    <div className="tt-wrapper">

      {/* ── DESKTOP ── */}
      <table className="tt-table">
        <thead>
          <tr>
            <th onClick={() => toggleSort('date')}        className="tt-sortable">Data        <SortIcon col="date" /></th>
            <th onClick={() => toggleSort('description')} className="tt-sortable">Descrição   <SortIcon col="description" /></th>
            <th onClick={() => toggleSort('category')}    className="tt-sortable">Categoria   <SortIcon col="category" /></th>
            <th onClick={() => toggleSort('value')}       className="tt-sortable">Valor       <SortIcon col="value" /></th>
            <th>Tipo</th>
            <th onClick={() => toggleSort('status')}      className="tt-sortable">Status      <SortIcon col="status" /></th>
            {transactions.some(t => t.business_id) && <th>Dono</th>}
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((t) => (
            <tr key={t.id}>
              <td className="tt-date">{formatDate(t.date)}</td>
              <td className="tt-desc" title={t.description}>
                <div className="tt-desc-row">
                  <span className="tt-desc-text">{t.description || '—'}</span>
                  <span className="tt-desc-icons">
                    {t.recorrente && (
                      <span className="tt-recurrence-icon" title="Transação Recorrente" style={{ fontSize: '0.9em', filter: 'grayscale(0.5)' }}>
                        🔁
                      </span>
                    )}
                    {t.comprovante_url && isSafeUrl(t.comprovante_url) && (
                      <a href={t.comprovante_url} target="_blank" rel="noreferrer noopener" title="Ver Comprovante" style={{ textDecoration: 'none' }}>
                        📎
                      </a>
                    )}
                  </span>
                </div>
                {t.tags && <div className="tt-tags">{t.tags.split(',').map(tag => <span key={tag} className="tt-tag">{tag.trim()}</span>)}</div>}
              </td>

              <td className="tt-cat">{t.category || '—'}</td>
              <td className={`tt-value ${isIncome(t.type) ? 'income' : 'expense'}`}>
                {isIncome(t.type) ? '+' : '−'}{formatCurrency(t.value)}
              </td>
              <td><span className={`tt-badge type-${isIncome(t.type) ? 'income' : 'expense'}`}>{t.type || '—'}</span></td>
              <td><span className={`tt-badge status-${getStatusClass(t.status)}`}>{t.status || '—'}</span></td>
              {transactions.some(tx => tx.business_id) && (
                <td className="tt-owner" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {t.business_id ? (t.nome_usuario || 'Membro') : 'Você'}
                </td>
              )}
              <td className="tt-actions">
                <button className="tt-btn-edit"      onClick={() => onEdit(t)}      title="Editar">✏️</button>
                <button className="tt-btn-duplicate" onClick={() => onDuplicate(t)} title="Duplicar">📋</button>
                <button className="tt-btn-delete"    onClick={() => onDelete(t)} title="Excluir">🗑️</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── MOBILE: cards ── */}
      <div className="tt-cards">
        {paginated.map((t) => {
          const isPending = getStatusClass(t.status) === 'pending';
          const isSelected = selectedTxId === t.id;

          return (
            <div 
              key={t.id} 
              className={`tt-card-wrapper ${isIncome(t.type) ? 'income' : 'expense'} ${isSelected ? 'selected' : ''}`}
            >
              <div 
                className="tt-card-content"
                onClick={() => setSelectedTxId(isSelected ? null : t.id)}
              >
                <div className="tt-card-icon">{isIncome(t.type) ? '📈' : '📉'}</div>
                <div className="tt-card-body">
                  <div className="tt-card-top">
                    <span className="tt-card-desc">
                      {t.description || '—'}
                      {t.recorrente && <span style={{ marginLeft: 6, fontSize: '0.8em' }}>🔁</span>}
                    </span>

                    <span className={`tt-card-value ${isIncome(t.type) ? 'income' : 'expense'}`}>
                      {isIncome(t.type) ? '+' : '−'}{formatCurrency(t.value)}
                    </span>
                  </div>
                  <div className="tt-card-bottom">
                    <span className="tt-card-meta">{formatDate(t.date)}</span>
                    {t.category && <span className="tt-card-meta">• {t.category}</span>}
                    <span className={`tt-card-status ${getStatusClass(t.status)}`}>{t.status || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Opções que aparecem ABAIXO ao clicar */}
              {isSelected && (
                <div className="tt-card-actions-expand">
                  <button className="tt-opt-btn edit" onClick={() => { onEdit(t); setSelectedTxId(null); }}>
                    ✏️ Editar
                  </button>
                  {isPending && onToggleStatus && (
                    <button className="tt-opt-btn pay" onClick={() => { onToggleStatus(t); setSelectedTxId(null); }}>
                      ✅ {isIncome(t.type) ? 'Receber' : 'Pagar'}
                    </button>
                  )}
                  <button className="tt-opt-btn delete" onClick={() => { onDelete(t); setSelectedTxId(null); }}>
                    🗑️ Excluir
                  </button>

                  <button className="tt-opt-btn close" onClick={() => setSelectedTxId(null)}>
                    ✖
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <PaginationControls />
    </div>
  );
};

export default TransactionsTable;
